// One project as a line of the home list: its standing as the glyph, its
// title over the plan's next step, the count and the tick bars. Tapping
// opens the plan. The row owns a menu: long-press on touch, right-click or
// the ⋯ under a mouse (see ui/Menu).
import type { RunProgress } from '@shared/artifacts';
import { nextStep, progress, stepsDone, type Project, type Stage } from '@shared/plan';
import { points } from '@shared/score';
import type { Standing } from '@shared/standing';
import { Icon, Row, StandingGlyph, Ticks, menuAnchor, useMenuTrigger, type GlyphStanding, type MenuAt } from '@app/ui';

export interface ProjectRowProps {
  project: Project;
  standing: Standing;
  /** The project's latest run, if it has one: what a building row says, and why a failed one needs a look. */
  run?: RunProgress;
  selected?: boolean;
  onOpen: (id: string) => void;
  onMenu: (id: string, at: MenuAt) => void;
}

const glyphOf = (s: Standing): GlyphStanding => (s === 'finished' ? 'finished' : s === 'building' ? 'building' : 'ring');

/** What a run is doing, as iOS's GenStage says it. */
export const STAGE_LINE: Record<Stage, string> = {
  understand: 'Studying the request…',
  research: 'Consulting the makers…',
  extract: 'Writing the checklist…',
  illustrate: 'Drawing the blueprint…',
};

/** What a snag's run is doing: it has no stages. */
export const SNAG_LINE = 'Working out your snag…';

/** Said when a run failed and the list gave no words of its own. */
export const FAILED_LINE = 'It didn’t finish. Open it to see why.';

/** The row's second line: the stage a run is at, why one failed, what the project wants next, or what it earned. */
export function subtitleOf(p: Project, standing: Standing, run?: RunProgress): string {
  if (standing === 'building') return run?.kind === 'snag' ? SNAG_LINE : STAGE_LINE[run?.stage ?? 'understand'];
  if (standing === 'needsLook') return run?.error ?? FAILED_LINE;
  if (standing === 'finished') return `Finished · ${points(p)} pts`;
  return nextStep(p)?.title ?? p.summary;
}

export function ProjectRow({ project: p, standing, run, selected, onOpen, onMenu }: ProjectRowProps) {
  const trigger = useMenuTrigger((at) => onMenu(p.id, at));
  // While it builds, the steps are the template's, not yet this project's: no count until they are.
  const done = stepsDone(p), total = standing === 'building' ? 0 : p.steps.length;
  return (
    <Row
      data-id={p.id}
      leading={<StandingGlyph standing={glyphOf(standing)} fraction={progress(p)} />}
      title={p.title} subtitle={subtitleOf(p, standing, run)} chevron selected={selected}
      onSelect={() => onOpen(p.id)}
      trailing={total > 0 ? <><span className="num">{done}/{total}</span><Ticks done={done} total={total} /></> : undefined}
      control={
        <button type="button" className="more" aria-label={`More: ${p.title}`} onClick={(e) => onMenu(p.id, menuAnchor(e.currentTarget))}>
          <Icon name="dots" />
        </button>
      }
      {...trigger}
    />
  );
}
